import { ApolloError, useLazyQuery, useMutation } from '@apollo/client'
import { useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  setFetchTeamRequest,
  setUpdateTeamRequest,
} from '../components/coaches/store/coaches.actions'
import {
  TeamCreatePayload,
  TeamInterface,
  TeamUpdatePayload,
} from '../components/coaches/store/coaches.types'
import {
  selectIsLoading,
  selectTeam,
} from '../components/coaches/store/coaches.selectors'
import { TEAM_MUTATIONS } from '../components/coaches/mutations/teams.mutations'
import { TEAM_QUERIES } from '../components/coaches/queries/teams.queries'
import { selectUser } from '../components/auth/store/auth.selectors'
import { UserInterface } from '../components/users/store/users.types'

const useTeam = (
  fetchOnMount?: boolean,
  teamId?: string,
  createTeamSuccess?: () => void
) => {
  const user: UserInterface | undefined = useSelector(selectUser)

  const dispatch = useDispatch()
  // eslint-disable-next-line @typescript-eslint/no-unused-vars,no-unused-vars
  const [error, setError] = useState<ApolloError>()
  const [initialLoading, setInitialLoading] = useState(true)
  const loading = useSelector(selectIsLoading)
  const team: TeamInterface | null = useSelector(selectTeam)
  const getTeam = useCallback((id: string) => {
    dispatch(setFetchTeamRequest({ where: { id } }))
  }, [])

  const updateTeam = useCallback((data: TeamUpdatePayload) => {
    dispatch(setUpdateTeamRequest(data))
  }, [])

  const [refetchTeams, { data: managerTeams, loading: managerTeamsLoading }] =
    useLazyQuery(TEAM_QUERIES.GET_TEAMS_BY_OWNER, {
      fetchPolicy: 'cache-and-network',
      variables: {
        ownerId: user?.id,
      },
      onCompleted: () => {
        setInitialLoading(false)
      },
    })

  const [createTeamMutation, { loading: createTeamLoading, error: teamError }] =
    useMutation<{
      createTeam: {
        name: string
      }
    }>(TEAM_MUTATIONS.CREATE_TEAM, {
      onCompleted() {
        refetchTeams()
        if (createTeamSuccess) {
          createTeamSuccess()
        }
      },
      onError() {},
    })

  const [getImageUploadUrlMutation, { loading: uploading }] = useMutation<{
    getImageUploadUrl: {
      uploadUrl: string
      fields: { name: string; value: string }[]
      key: string
      fileUrl: string
    }
  }>(TEAM_MUTATIONS.GET_IMAGE_UPLOAD_URL)

  const [confirmAvatarUploadMutation, { loading: confirming }] = useMutation<{
    confirmAvatarUpload: {
      success: boolean
      fileUrl: string
      message?: string
    }
  }>(TEAM_MUTATIONS.CONFIRM_AVATAR_UPLOAD)

  const uploadAvatarImage = async (file: File, teamId: string) => {
    try {
      // Step 1: Get the presigned URL
      const { data: uploadData } = await getImageUploadUrlMutation({
        variables: {
          uploadType: 'AVATAR',
          filename: file.name,
          mimetype: file.type,
          maxSizeBytes: 10 * 1024 * 1024, // 10MB
        },
      })

      if (!uploadData?.getImageUploadUrl) {
        throw new Error('Failed to get upload URL')
      }

      const { uploadUrl, fields, key, fileUrl } = uploadData.getImageUploadUrl

      // Step 2: Upload to S3 using the presigned URL
      const formData = new FormData()
      fields.forEach((field) => {
        formData.append(field.name, field.value)
      })
      formData.append('file', file)

      const uploadResponse = await fetch(uploadUrl, {
        method: 'POST',
        body: formData,
      })

      if (!uploadResponse.ok) {
        throw new Error('Failed to upload to S3')
      }

      // Step 3: Confirm the upload and update the team
      const { data: confirmData } = await confirmAvatarUploadMutation({
        variables: {
          key,
          fileUrl,
          isTeamAvatar: true,
          teamId,
        },
      })

      if (!confirmData?.confirmAvatarUpload?.success) {
        throw new Error('Failed to confirm avatar upload')
      }

      return confirmData.confirmAvatarUpload.fileUrl
    } catch (error) {
      console.error('Error uploading team avatar:', error)
      throw error
    }
  }

  const uploadAvatarToS3 = async (file: File) => {
    try {
      // Step 1: Get the presigned URL
      const { data: uploadData } = await getImageUploadUrlMutation({
        variables: {
          uploadType: 'AVATAR',
          filename: file.name,
          mimetype: file.type,
          maxSizeBytes: 10 * 1024 * 1024, // 10MB
        },
      })

      if (!uploadData?.getImageUploadUrl) {
        throw new Error('Failed to get upload URL')
      }

      const { uploadUrl, fields, fileUrl } = uploadData.getImageUploadUrl
      console.log('Presigned URL data:', fileUrl)

      // Step 2: Upload to S3 using the presigned URL
      const formData = new FormData()
      fields.forEach((field) => {
        formData.append(field.name, field.value)
      })
      formData.append('file', file)

      const uploadResponse = await fetch(uploadUrl, {
        method: 'POST',
        body: formData,
      })

      if (!uploadResponse.ok) {
        throw new Error('Failed to upload to S3')
      }

      return fileUrl
    } catch (error) {
      console.error('Error uploading team avatar:', error)
      throw error
    }
  }

  const createTeam = async (data: TeamCreatePayload) => {
    await createTeamMutation({
      variables: data,
    })
  }

  useEffect(() => {
    if (fetchOnMount && teamId) {
      getTeam(teamId)
    }
  }, [fetchOnMount, teamId, getTeam])

  return {
    getTeam,
    updateTeam,
    createTeam,
    uploadAvatarImage,
    uploadAvatarToS3,
    team,
    loading,
    uploading: uploading || confirming,
    error,
    managerTeams: managerTeams?.teamsByOwner,
    managerTeamsLoading,
    refetchTeams,
    initialLoading,
    createTeamLoading,
    teamError,
  }
}

export default useTeam
